import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { Bell, Inbox } from "lucide-react";
import { useAuth, useFeature, hasPermission } from "@/lib/auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLiveChatNotifications } from "@/hooks/use-live-chat-notifications";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

type NotificationItem = {
  sessionId: string;
  displayName: string;
  status: string;
  requestedAt: string;
  readAt: string | null;
  unread: boolean;
  canOpen: boolean;
  assignedToMe?: boolean;
  kind?: string;
};
type NotificationList = { items: NotificationItem[]; unreadCount: number };

function statusLabel(status: string) {
  if (status === "queued") return "Waiting";
  if (status === "active") return "Active";
  if (status === "escalated") return "Escalated";
  if (status === "closed") return "Ended";
  return "Unavailable";
}

function timeLabel(value: string) {
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return "";
  const minutes = Math.max(0, Math.floor((Date.now() - time) / 60000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
  return `${Math.floor(minutes / 1440)}d ago`;
}

export function LiveChatNotificationBell() {
  const { user, organization } = useAuth();
  const featureEnabled = useFeature("livechat.operator");
  const operatorEnabled = !!user?.id && !!organization?.id && hasPermission(user, "livechat.operate") && featureEnabled;
  const offlineFeature = useFeature("livechat.offline");
  const offlineEnabled = !!user?.id && !!organization?.id && hasPermission(user, "livechat.admin") && offlineFeature;
  const enabled = operatorEnabled || offlineEnabled;
  const notifications = useLiveChatNotifications(operatorEnabled);
  const offline = useQuery<NotificationList>({
    queryKey: ["/api/live-chat/offline-notifications", organization?.id, user?.id],
    enabled: offlineEnabled, refetchInterval: 15000, refetchIntervalInBackground: true,
    refetchOnWindowFocus: "always", refetchOnReconnect: "always", staleTime: 0,
    queryFn: async () => (await apiRequest("GET", "/api/live-chat/offline-notifications")).json(),
  });
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [failure, setFailure] = useState("");
  const scope = `${user?.id ?? ""}:${organization?.id ?? ""}:${enabled}`;
  useEffect(() => {
    if (!offlineEnabled || typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(`live-chat-read:${organization?.id}:${user?.id}`);
    channel.onmessage = () => { void queryClient.invalidateQueries({ queryKey: ["/api/live-chat/offline-notifications"] }); };
    return () => channel.close();
  }, [offlineEnabled, organization?.id, user?.id]);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const busyRef = useRef(false);
  useEffect(() => {
    setOpen(false);
    setPending(null);
    setFailure("");
  }, [scope]);

  async function openRequest(item: NotificationItem) {
    if (!enabled || busyRef.current) return;
    const startedScope = scope;
    busyRef.current = true;
    setPending(item.sessionId);
    setFailure("");
    try {
      // Message read state advances only after the destination renders its
      // transcript; opening a message must not write the request receipt.
      if (item.kind !== "message")
        await apiRequest("POST", `/api/live-chat/${item.kind === "offline" ? "offline-notifications" : "notifications"}/${encodeURIComponent(item.sessionId)}/read`);
      await queryClient.invalidateQueries({ queryKey: ["/api/live-chat/notifications"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/live-chat/offline-notifications"] });
      if (typeof BroadcastChannel !== "undefined") {
        const channel = new BroadcastChannel(`live-chat-read:${organization?.id}:${user?.id}`);
        channel.postMessage("read");
        channel.close();
      }
      if (scopeRef.current !== startedScope) return;
      setOpen(false);
      navigate(item.kind === "offline" ? "/live-chat/offline" : `/live-chat?session=${encodeURIComponent(item.sessionId)}`);
    } catch {
      if (scopeRef.current === startedScope) {
        setFailure("Could not open this request. Please try again.");
      }
    } finally {
      busyRef.current = false;
      if (scopeRef.current === startedScope) setPending(null);
    }
  }

  if (!enabled) return null;
  const unread = (operatorEnabled && !notifications.isError ? notifications.data?.unreadCount ?? 0 : 0)
    + (offlineEnabled && !offline.isError ? offline.data?.unreadCount ?? 0 : 0);
  const items = [...(operatorEnabled && !notifications.isError ? notifications.data?.items ?? [] : []),
    ...(offlineEnabled && !offline.isError ? offline.data?.items ?? [] : [])];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost" size="icon" className="relative"
          aria-label={`Live chat requests${unread ? `, ${unread} unread` : ""}`}
          data-testid="button-live-chat-notifications"
        >
          <Bell className="h-4 w-4" aria-hidden="true" />
          {unread > 0 && (
            <span aria-hidden="true" className="absolute -right-1 -top-1 min-w-4 rounded-full bg-primary px-1 text-[10px] font-semibold leading-4 text-primary-foreground" data-testid="badge-live-chat-unread">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {unread > 0 ? `${unread} unread live chat requests` : "No unread live chat requests"}
      </span>
      <PopoverContent align="end" className="w-80 max-w-[calc(100vw-2rem)] p-0" aria-label="Recent live chat requests">
        <div className="border-b px-4 py-3">
          <h2 className="text-sm font-semibold">Live chat notifications</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">Requests, messages and offline enquiries</p>
        </div>
        {failure && <p className="border-b px-4 py-3 text-xs text-destructive" role="alert">{failure}</p>}
        {(operatorEnabled && notifications.isLoading) || (offlineEnabled && offline.isLoading) ? (
          <div className="space-y-3 p-4" role="status" aria-label="Loading requests">
            {[0, 1, 2].map((n) => <div key={n} className="h-10 animate-pulse rounded bg-muted" aria-hidden="true" />)}
          </div>
        ) : (operatorEnabled && notifications.isError) || (offlineEnabled && offline.isError) ? (
          <div className="space-y-3 p-4">
            <p className="text-xs text-muted-foreground" role="alert">Requests could not be loaded.</p>
            <Button size="sm" variant="outline" onClick={() => { if (operatorEnabled) void notifications.refetch(); if (offlineEnabled) void offline.refetch(); }}>Try again</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-7 text-center">
            <Inbox className="mx-auto mb-2 h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm font-medium">No recent requests</p>
            <p className="mt-1 text-xs text-muted-foreground">New handoffs will appear here.</p>
          </div>
        ) : (
          <ul className="max-h-80 overflow-y-auto p-1.5" aria-busy={!!pending}>
            {items.map((item) => (
              <li key={`${item.kind}:${item.sessionId}`}>
                <button type="button" disabled={!!pending} onClick={() => { void openRequest(item); }}
                  className="flex w-full items-start gap-2 rounded-md px-2.5 py-3 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                  data-testid="button-live-chat-request">
                  <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${item.unread ? "bg-primary" : "bg-transparent"}`} aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-sm ${item.unread ? "font-semibold" : "font-medium"}`}>{item.displayName || "Visitor"}</span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {pending === item.sessionId ? "Opening…" : `${item.kind === "offline" ? "Offline message" : item.kind === "message" && item.unread ? "New visitor message" : statusLabel(item.status)}${timeLabel(item.requestedAt) ? ` · ${timeLabel(item.requestedAt)}` : ""}`}
                    </span>
                  </span>
                  {item.unread && <span className="sr-only">Unread</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Resolves URL targets against the authoritative notification API. This is
 * deliberately not an arrival subscription; the shell hook owns that.
 */
export function useLiveChatNotificationTarget(onSelect: (id: string | null, monitor: boolean) => void) {
  const search = useSearch();
  const requestedSessionId = new URLSearchParams(search).get("session");
  const { user, organization } = useAuth();
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;
  const [queuedSessionId, setQueuedSessionId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [isResolving, setIsResolving] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setQueuedSessionId(null);
    setMessage("");
    setIsResolving(false);
    if (!requestedSessionId) return;
    selectRef.current(null, false);
    if (!user?.id || !organization?.id) return;
    setIsResolving(true);
    void (async () => {
      try {
        const data: NotificationList = await (await apiRequest("GET", "/api/live-chat/notifications")).json();
        if (cancelled) return;
        const target = data.items.find((item) => item.sessionId === requestedSessionId);
        if (target?.status === "queued" && target.canOpen) {
          setQueuedSessionId(requestedSessionId);
          setMessage("This request is waiting. Claim it from the highlighted queue item to begin.");
        } else if (target?.canOpen && (target.status === "active" || target.status === "escalated" || target.status === "closed")) {
          // The API authorizes own sessions and supervisor-readable sessions.
          selectRef.current(requestedSessionId, target.status === "closed" || !target.assignedToMe);
        } else {
          setMessage("This request is no longer available to open. Check the current queue.");
        }
      } catch {
        if (!cancelled) setMessage("This request could not be opened. Please try again.");
      } finally {
        if (!cancelled) {
          setIsResolving(false);
          void queryClient.invalidateQueries({ queryKey: ["/api/live-chat/queue"] });
          void queryClient.invalidateQueries({ queryKey: ["/api/live-chat/sessions/mine"] });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [requestedSessionId, user?.id, organization?.id, attempt]);
  return { requestedSessionId, queuedSessionId, message, isResolving, retry: () => setAttempt((n) => n + 1) };
}
